import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { useTranslation } from "react-i18next"
import { resolveCapabilityReasonLabel } from "schema"
import { CapabilityExecutionSummary } from "./CapabilityExecutionSummary"
import { MessageCapabilityExecution } from "./capabilityExecutionPresentation"

type Props = { execution: MessageCapabilityExecution }

export function CapabilityExecutionDetails({ execution }: Props) {
  const { t } = useTranslation()
  const { developerMode } = useDeveloperMode()
  const status = execution.status
  const isUnsuccess = status === "Unsuccess"
  const isError = status === "Error"
  const isSkipped = status === "Skipped"
  const hasResult = !!execution.result && typeof execution.result === "object"
  const isEmptyResult =
    hasResult &&
    (Array.isArray(execution.result)
      ? execution.result.length === 0
      : Object.keys(execution.result).length === 0)
  const hasEvaluation = !!execution.evaluation && typeof execution.evaluation === "object"
  const isEmptyEvaluation =
    hasEvaluation &&
    (Array.isArray(execution.evaluation)
      ? execution.evaluation.length === 0
      : Object.keys(execution.evaluation).length === 0)
  const hasRaw = !!execution.raw && typeof execution.raw === "object"
  const isEmptyRaw =
    hasRaw &&
    (Array.isArray(execution.raw)
      ? execution.raw.length === 0
      : Object.keys(execution.raw).length === 0)
  const skipOrErrorReason =
    isUnsuccess || isError || isSkipped
      ? resolveCapabilityReasonLabel(execution.capabilityIdentifier, execution.reason)
      : null

  const resultJson =
    execution.result && !(isError || isSkipped) ? JSON.stringify(execution.result, null, 2) : null
  const evaluationJson = execution.evaluation ? JSON.stringify(execution.evaluation, null, 2) : null
  const rawJson = execution.raw ? JSON.stringify(execution.raw, null, 2) : null

  return (
    <>
      <CapabilityExecutionSummary execution={execution} />
      {isError && skipOrErrorReason && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-destructive">
            {t("conversations.chat.capabilityExecutions.errorMessage")}
          </p>
          <MarkdownRenderer codeClassName="overflow-x-auto whitespace-pre-wrap break-words">
            {`\`\`\`txt\n${skipOrErrorReason}\n\`\`\``}
          </MarkdownRenderer>
        </div>
      )}
      {isUnsuccess && skipOrErrorReason && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-testing-1">
            {t("conversations.chat.capabilityExecutions.unsuccessReason")}
          </p>
          <MarkdownRenderer codeClassName="overflow-x-auto whitespace-pre-wrap break-words">
            {`\`\`\`txt\n${skipOrErrorReason}\n\`\`\``}
          </MarkdownRenderer>
        </div>
      )}
      {isSkipped && skipOrErrorReason && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium text-warning">
            {t("conversations.chat.capabilityExecutions.skipReason")}
          </p>
          <MarkdownRenderer codeClassName="overflow-x-auto whitespace-pre-wrap break-words">
            {`\`\`\`txt\n${skipOrErrorReason}\n\`\`\``}
          </MarkdownRenderer>
        </div>
      )}
      {hasResult && !isEmptyResult && resultJson && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">
            {t("conversations.chat.capabilityExecutions.result")}
          </p>
          <MarkdownRenderer>{`\`\`\`json\n${resultJson}\n\`\`\``}</MarkdownRenderer>
        </div>
      )}
      {hasEvaluation && !isEmptyEvaluation && evaluationJson && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">
            {t("conversations.chat.capabilityExecutions.evaluation")}
          </p>
          <MarkdownRenderer>{`\`\`\`json\n${evaluationJson}\n\`\`\``}</MarkdownRenderer>
        </div>
      )}
      {developerMode && hasRaw && !isEmptyRaw && rawJson && (
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium">{t("conversations.chat.capabilityExecutions.raw")}</p>
          <MarkdownRenderer>{`\`\`\`json\n${rawJson}\n\`\`\``}</MarkdownRenderer>
        </div>
      )}
    </>
  )
}
